import { Box, Drawer } from '@mui/material'
import { L } from '../ui/LavDropdown'

/**
 * Shared mobile bits for the Appointment Details + Checkout screens, from the
 * Claude Design "Appointment Details" spec (ApdIc / Ic / status pills / bottom
 * sheets). Scoped to the appointment-details mobile flow.
 */

// ---- icon set (design glyphs: ApdIc + checkout Ic + mobile chrome) ----
const PATHS = {
	dots: (
		<>
			<circle cx="3.2" cy="8" r="1.25" fill="currentColor" stroke="none" />
			<circle cx="8" cy="8" r="1.25" fill="currentColor" stroke="none" />
			<circle cx="12.8" cy="8" r="1.25" fill="currentColor" stroke="none" />
		</>
	),
	ban: (
		<>
			<circle cx="8" cy="8" r="6" />
			<path d="M3.9 3.9l8.2 8.2" />
		</>
	),
	undo: (
		<>
			<path d="M3.2 6.8h6.6a3.3 3.3 0 010 6.6H6.2" />
			<path d="M5.6 4.4L3.2 6.8l2.4 2.4" />
		</>
	),
	back: <path d="M10 3L5 8l5 5" />,
	chevup: <path d="M4.5 9.5L8 6l3.5 3.5" />,
	checkc: (
		<>
			<circle cx="8" cy="8" r="6.5" />
			<path d="M5.2 8.3l2 2 3.6-4" />
		</>
	),
	warn: (
		<>
			<path d="M8 2.2L14.6 13H1.4L8 2.2z" />
			<path d="M8 6.6v3" />
			<circle cx="8" cy="11.4" r="0.55" fill="currentColor" stroke="none" />
		</>
	),
	send: <path d="M14 2L7.5 8.5M14 2L9.8 14l-2.3-5.5L2 6.2 14 2z" />,
	search: (
		<>
			<circle cx="7" cy="7" r="4.6" />
			<path d="M10.5 10.5L14 14" />
		</>
	),
	plus: <path d="M8 3.5v9M3.5 8h9" />,
	minus: <path d="M3.5 8h9" />,
	// payment method glyphs (design checkout-shared Ic set)
	cash: (
		<>
			<rect x="1.5" y="4" width="13" height="8" rx="1.5" />
			<circle cx="8" cy="8" r="2" />
		</>
	),
	card: (
		<>
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M1.5 6.5h13" />
		</>
	),
	card2: (
		<>
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M4 10h3" />
		</>
	),
	check: (
		<>
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M4 6.5h8M4 9h5" />
		</>
	),
	tap: (
		<>
			<path d="M5 13.5V6a3 3 0 016 0v7.5" />
			<path d="M2.5 8.5a5.5 5.5 0 0111 0" opacity=".5" />
		</>
	),
	phone: (
		<>
			<rect x="4.5" y="1.5" width="7" height="13" rx="1.5" />
			<path d="M7 12.5h2" />
		</>
	),
	terminal: (
		<>
			<rect x="3.5" y="1.5" width="9" height="13" rx="1.5" />
			<path d="M5.5 4h5v3h-5z" />
		</>
	),
	tag: (
		<>
			<path d="M8.6 1.8L14 7.2a1.5 1.5 0 010 2.1l-4.7 4.7a1.5 1.5 0 01-2.1 0L1.8 8.6V1.8h6.8z" />
			<circle cx="5" cy="5" r="1.1" />
		</>
	),
	chevron: <path d="M4.5 6.5L8 10l3.5-3.5" />,
	tick: <path d="M3 8.5l3.2 3L13 4.5" />,
	// post-checkout invoice actions (design ApdIc receipt / download)
	receipt: (
		<>
			<path d="M3.5 1.5h9v13l-1.5-1.1-1.5 1.1-1.5-1.1-1.5 1.1-1.5-1.1-1.5 1.1v-13z" />
			<path d="M6 5.3h4M6 8h4M6 10.7h2.5" />
		</>
	),
	download: <path d="M8 2.5v8M4.8 7.3L8 10.5l3.2-3.2M2.5 13.5h11" />,
	x: <path d="M4 4l8 8M12 4l-8 8" />,
	// reschedule flow (design ApdIc move / chevL / chevR / text / mail)
	move: (
		<>
			<path d="M2.5 12.5c0-4.6 2.6-7 7.3-7h3.2" />
			<path d="M10.6 3.2L13 5.5l-2.4 2.3" />
		</>
	),
	chevL: <path d="M10 3.5L5.5 8l4.5 4.5" />,
	chevR: <path d="M6 3.5L10.5 8 6 12.5" />,
	text: <path d="M2.5 3.5h11v8h-6L4 14v-2.5H2.5v-8z" />,
	mail: (
		<>
			<rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
			<path d="M1.5 5.2l6.5 4.3 6.5-4.3" />
		</>
	),
}

export function MbIc({ name, size = 16, sw = 1.8 }) {
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 16 16"
			fill="none"
			stroke="currentColor"
			strokeWidth={sw}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			style={{ flex: 'none', display: 'block' }}
		>
			{PATHS[name] || null}
		</svg>
	)
}

/** Icon name for a payment-method value (paymentMethodUtils values). */
export function payMethodIcon(value) {
	switch (value) {
		case 'cash': return 'cash'
		case 'card_terminal': return 'card'
		case 'check': return 'check'
		case 'apple_pay':
		case 'google_pay': return 'tap'
		case 'paypal':
		case 'venmo':
		case 'cash_app': return 'phone'
		case 'e_transfer': return 'send'
		case 'gift_card': return 'tag'
		case 'square_terminal': return 'terminal'
		case 'square_card': return 'card2'
		default: return 'card'
	}
}

// ---- status pill (apd-pill) ----
const PILL_STYLES = {
	booked: { label: 'Booked', bg: L.primarySoft, color: L.primaryDeep },
	bookPending: { label: 'Pending', bg: '#FDF3E4', color: '#8F6217' },
	completed: { label: 'Completed', bg: '#E9F6EE', color: '#25714A' },
	noShow: { label: 'No-show', bg: '#FDF3E4', color: '#8F6217' },
	cancelled: { label: 'Canceled', bg: '#EFEDF5', color: '#6E6889' },
}

export function ApdPill({ status, mobile = false }) {
	const s = PILL_STYLES[status] || PILL_STYLES.booked
	return (
		<Box component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none',
			p: mobile ? '4px 12px 4px 9px' : '3px 10px 3px 8px', borderRadius: '99px',
			fontSize: mobile ? '12px' : '11.5px', fontWeight: 750, letterSpacing: '0.02em',
			bgcolor: s.bg, color: s.color, fontFamily: L.font,
		}}>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
			<span>{s.label}</span>
		</Box>
	)
}

// ---- bottom sheet (mbapd-sheet / mbck-sheet) ----
export function MobileSheet({ open, onClose, title, children, center = false }) {
	return (
		<Drawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					bgcolor: L.bg, borderRadius: '26px 26px 0 0', p: '10px 16px 24px',
					maxHeight: '78%', display: 'flex', flexDirection: 'column',
					boxShadow: '0 -12px 40px rgba(46, 42, 64, 0.22)', fontFamily: L.font,
					...(center ? { textAlign: 'center', p: '10px 20px 24px' } : {}),
				},
			}}
		>
			<Box sx={{ width: 40, height: 5, flex: 'none', borderRadius: '99px', bgcolor: L.border, m: '0 auto 12px' }} />
			{title && (
				<Box sx={{
					flex: 'none', fontSize: '12px', fontWeight: 750, letterSpacing: '0.08em',
					textTransform: 'uppercase', color: L.muted, p: '2px 4px 12px', textAlign: 'left',
				}}>
					{title}
				</Box>
			)}
			{children}
		</Drawer>
	)
}

/** Full-width grey "Close / Keep editing" sheet button. */
export function SheetCancelButton({ onClick, children }) {
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			sx={{
				width: '100%', height: 52, flex: 'none', border: 'none', borderRadius: '14px',
				bgcolor: L.surface, color: L.text, fontSize: '15.5px', fontWeight: 750,
				fontFamily: L.font, cursor: 'pointer', mt: '10px',
				'&:active': { bgcolor: L.border },
			}}
		>
			{children}
		</Box>
	)
}

/** 52px primary action button (mbapd-btn-primary / mbck-btn-primary). */
export function MbPrimaryButton({ onClick, disabled, children, bg = L.primary, activeBg = L.primaryDeep, sx = {} }) {
	return (
		<Box
			component="button"
			type="button"
			onClick={onClick}
			disabled={disabled}
			sx={{
				flex: 1, minWidth: 0, height: 52, minHeight: 52, px: '12px', border: 'none', borderRadius: '14px',
				bgcolor: bg, color: '#FFFFFF', fontSize: '15.5px', fontWeight: 750,
				display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
				whiteSpace: 'nowrap', boxShadow: '0 3px 12px rgba(124, 107, 217, 0.32)',
				fontFamily: L.font, cursor: 'pointer',
				'&:active': { bgcolor: activeBg },
				'&:disabled': { opacity: 0.7, boxShadow: 'none', cursor: 'default' },
				...sx,
			}}
		>
			{children}
		</Box>
	)
}
